import { useEffect, useState, useSyncExternalStore } from 'react'
import { getLoadingSnapshot, subscribeLoading } from '../scene/loadingProgress'
import type { LoadingFailureCode } from '../scene/loadingProgress'
import type { InputMode } from '../shared/types'
import ControlsGuide from './ControlsGuide'

const PHASE_COPY = {
  idle: '准备加载展厅',
  engine: '启动展厅引擎',
  manifests: '读取展品清单',
  models: '下载展品模型',
  preparing: '整理展厅场景',
  ready: '展厅即将就绪',
  error: '展厅载入失败',
} as const

const FAILURE_COPY: Record<LoadingFailureCode, string> = {
  RESOURCE_LOAD_FAILED: '展厅资源未能读取。稍后重试，或联系维护人员并提供下方代码。',
  MODEL_PARSE_FAILED: '展品模型无法解析，可能与文件格式或内容有关。',
  ASSET_LOAD_FAILED: '展品资源或模型未能载入。',
  ENGINE_INITIALIZATION_FAILED: '展厅程序未能启动，请重新加载。',
  SCENE_INITIALIZATION_FAILED: '展厅未能完成初始化，请重新加载。',
}

const MEBIBYTE = 1024 * 1024
const mebibytes = new Intl.NumberFormat('zh-CN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

function formatMebibytes(bytes: number) {
  return `${mebibytes.format(Math.max(0, bytes) / MEBIBYTE)} MiB`
}

export default function MuseumLoading({ failed = false, inputMode = 'pointer' }: { failed?: boolean; inputMode?: InputMode }) {
  const snapshot = useSyncExternalStore(subscribeLoading, getLoadingSnapshot, getLoadingSnapshot)
  const [isSlow, setIsSlow] = useState(false)
  const isFailed = failed || snapshot.phase === 'error'
  const failureCode = snapshot.error ?? 'SCENE_INITIALIZATION_FAILED'
  const isDeterminate = snapshot.phase === 'models' && snapshot.totalBytes > 0
  const loadedBytes = Math.min(Math.max(0, snapshot.loadedBytes), Math.max(0, snapshot.totalBytes))
  const progress = isDeterminate ? Math.round((loadedBytes / snapshot.totalBytes) * 100) : 0

  useEffect(() => {
    if (isFailed) return
    setIsSlow(false)
    const timer = window.setTimeout(() => setIsSlow(true), 12_000)
    return () => window.clearTimeout(timer)
  }, [isFailed])

  return (
    <section className={`museum-loading${isFailed ? ' museum-loading--error' : ''}`} aria-label={isFailed ? '展厅载入失败' : '展厅正在载入'}>
      <div className="museum-loading__content">
        <img className="museum-loading__art" src="/assets/unity/art-14.png" alt="" aria-hidden="true" />
        <p className="museum-loading__eyebrow">C430 / HUMAN MUSEUM</p>
        <h1 className="museum-loading__title">{isFailed ? '展厅暂未准备好' : '正在准备展厅'}</h1>

        {isFailed ? (
          <div className="museum-loading__failure" role="alert">
            <p>{FAILURE_COPY[failureCode]}</p>
            <p><code>{failureCode}</code></p>
            <button type="button" className="museum-loading__retry" onClick={() => window.location.reload()}>重新加载</button>
          </div>
        ) : (
          <>
            <div className="museum-loading__phase-row">
              <p className="museum-loading__phase" role="status" aria-live="polite">{PHASE_COPY[snapshot.phase]}</p>
              {isDeterminate && <span className="museum-loading__percentage" aria-hidden="true">{progress}%</span>}
            </div>
            <div
              className={`museum-loading__bar${isDeterminate ? '' : ' museum-loading__bar--indeterminate'}`}
              role="progressbar"
              aria-label={isDeterminate ? '展品模型下载进度' : PHASE_COPY[snapshot.phase]}
              aria-valuemin={0}
              aria-valuemax={isDeterminate ? 100 : undefined}
              aria-valuenow={isDeterminate ? progress : undefined}
            >
              <span style={isDeterminate ? { transform: `scaleX(${progress / 100})` } : undefined} />
            </div>
            {isDeterminate ? (
              <div className="museum-loading__details" aria-live="off">
                <span>{Math.max(0, snapshot.loadedModels)} / {Math.max(0, snapshot.totalModels)} 件模型</span>
                <span>{formatMebibytes(loadedBytes)} / {formatMebibytes(snapshot.totalBytes)}</span>
              </div>
            ) : <p className="museum-loading__hint">请稍候</p>}
            {isSlow && <p className="museum-loading__slow">展厅仍在准备中，请保持页面打开并确认网络连接正常。</p>}
            <ControlsGuide variant="loading" inputMode={inputMode} />
          </>
        )}
      </div>
    </section>
  )
}
